import { useCallback } from 'react';
import type { ProfileSelections, DerivedPersona, Segment, ComparisonResult } from '@/types/consumer';
import { DECISION_FACTORS, MEDIA_CHANNELS } from '@/types/consumer';

// ============================================================================
// QUOTE TEMPLATES
// ============================================================================

const QUOTE_TEMPLATES: Record<string, { en: string[]; es: string[] }> = {
  traditional_loyalist: {
    en: [
      "My family has always bought {brand}. It's part of our tradition.",
      "I don't need to try new things - {brand} has never let me down.",
    ],
    es: [
      "Mi familia siempre ha comprado {brand}. Es parte de nuestra tradición.",
      "No necesito probar cosas nuevas - {brand} nunca me ha fallado.",
    ],
  },
  value_seeker: {
    en: [
      "I always compare prices. If {brand} isn't on sale, I'll look at alternatives.",
      "Quality matters, but so does my wallet. I go where the deals are.",
    ],
    es: [
      "Siempre comparo precios. Si {brand} no está en oferta, busco alternativas.",
      "La calidad importa, pero también mi cartera. Voy donde están las ofertas.",
    ],
  },
  health_conscious: {
    en: [
      "I read every label. Zero sugar options from {brand} are my go-to.",
      "I've cut back on sodas, but when I do indulge, it's the healthier options.",
    ],
    es: [
      "Leo cada etiqueta. Las opciones sin azúcar de {brand} son mi elección.",
      "He reducido los refrescos, pero cuando me doy el gusto, elijo opciones más saludables.",
    ],
  },
  convenience_driven: {
    en: [
      "I grab {brand} at the corner store on my way to work. Quick and easy.",
      "I don't plan ahead - I buy what's available when I need it.",
    ],
    es: [
      "Compro {brand} en la tienda de la esquina camino al trabajo. Rápido y fácil.",
      "No planifico - compro lo que hay disponible cuando lo necesito.",
    ],
  },
  young_experimenter: {
    en: [
      "I love trying new flavors. {brand}'s latest launch? I was first in line.",
      "My friends on social media got me into craft sodas. So much variety!",
    ],
    es: [
      "Me encanta probar sabores nuevos. ¿El último lanzamiento de {brand}? Fui el primero en la fila.",
      "Mis amigos en redes sociales me metieron en los refrescos artesanales. ¡Tanta variedad!",
    ],
  },
  default: {
    en: [
      "I buy what fits my needs and budget.",
      "Brand matters, but so does value.",
    ],
    es: [
      "Compro lo que se ajusta a mis necesidades y presupuesto.",
      "La marca importa, pero también el valor.",
    ],
  },
};

// ============================================================================
// DERIVED METRICS - Price Sensitivity & Brand Loyalty Calculation
// ============================================================================

/**
 * Calculate price sensitivity based on demographic inputs (OUTPUT, not input)
 * Returns 0-1 where 1 = highly price sensitive
 */
function calculatePriceSensitivity(selections: ProfileSelections): number {
  let score = 0.5; // Base sensitivity

  // Income level has highest impact
  const incomeOrder = ['D+', 'D', 'C-', 'C', 'C+', 'B', 'A/B'];
  const incomeIdx = incomeOrder.indexOf(selections.incomeLevel);
  if (selections.incomeLevel !== 'all' && incomeIdx !== -1) {
    // Lower income = higher price sensitivity
    score += (incomeOrder.length - 1 - incomeIdx) * 0.08;
  }
  // 'all' has neutral impact

  // Household size impacts budget
  if (selections.householdSize === '5+') score += 0.1;
  else if (selections.householdSize === '3-4') score += 0.05;

  // Children increase budget pressure
  if (selections.hasChildren === 'yes') score += 0.08;
  // 'all' and 'no' have neutral/no impact

  // Lifestyle segment
  if (selections.lifestyleSegment === 'price_focused') score += 0.15;
  else if (selections.lifestyleSegment === 'premium') score -= 0.15;
  else if (selections.lifestyleSegment === 'health_conscious') score -= 0.05;

  // Channel preference
  if (selections.channelPreference === 'traditional') score += 0.05;
  else if (selections.channelPreference === 'ecommerce') score -= 0.05;

  // Consumption frequency - heavier consumers more price sensitive
  if (selections.consumptionFrequency === 'daily') score += 0.08;
  else if (selections.consumptionFrequency === 'rarely') score -= 0.1;

  // Age impact (younger and older tend to be more price conscious)
  const hasYoung = selections.ageRanges.includes('18-28');
  const hasSenior = selections.ageRanges.includes('55+');
  if (hasYoung) score += 0.05;
  if (hasSenior) score += 0.03;

  // Location type
  if (selections.locationTypes.includes('rural')) score += 0.05;
  if (selections.locationTypes.includes('urban') && selections.locationTypes.length === 1) score -= 0.03;

  return Math.max(0, Math.min(1, score));
}

/**
 * Calculate brand loyalty based on demographic inputs (OUTPUT, not input)
 * Returns 0-1 where 1 = highly brand loyal
 */
function calculateBrandLoyalty(selections: ProfileSelections): number {
  let score = 0.5; // Base loyalty

  // Lifestyle segment has highest impact
  if (selections.lifestyleSegment === 'traditional') score += 0.2;
  else if (selections.lifestyleSegment === 'price_focused') score -= 0.15;

  // Age impact - older tends to be more loyal
  const hasYoung = selections.ageRanges.includes('18-28');
  const hasMidAge = selections.ageRanges.includes('35-45') || selections.ageRanges.includes('45-55');
  const hasSenior = selections.ageRanges.includes('55+');
  if (hasYoung && !hasSenior) score -= 0.1;
  if (hasSenior) score += 0.1;
  if (hasMidAge) score += 0.05;

  // Marital status - married tends to be more loyal (established habits)
  if (selections.maritalStatus === 'married') score += 0.08;
  else if (selections.maritalStatus === 'single') score -= 0.05;

  // Children in household - routines drive loyalty
  if (selections.hasChildren === 'yes') score += 0.1;
  // 'all' and 'no' have neutral/no impact

  // Channel preference
  if (selections.channelPreference === 'traditional') score += 0.1;
  else if (selections.channelPreference === 'ecommerce') score -= 0.05;

  // Consumption frequency - frequent consumers tend to be loyal
  if (selections.consumptionFrequency === 'daily') score += 0.1;
  else if (selections.consumptionFrequency === 'occasionally') score -= 0.1;

  // Brand preference specified
  if (selections.brandPreference && selections.brandPreference !== '') {
    score += 0.1; // Having a preference indicates loyalty tendency
  }

  // Region
  if (selections.region === 'south') score += 0.05; // Traditional markets tend more loyal
  if (selections.region === 'center') score -= 0.03; // More competitive market

  return Math.max(0, Math.min(1, score));
}

// ============================================================================
// TARGETING INFO - Key Factors & Media Channels
// ============================================================================

/**
 * Calculate what factors this consumer cares about most
 * Returns sorted array of factors with importance scores
 */
function calculateKeyFactors(
  selections: ProfileSelections,
  priceSensitivity: number,
  brandLoyalty: number,
  language: string
): Array<{ factor: string; importance: number }> {
  const factors: Record<string, number> = {
    price: 0.3,
    taste: 0.5,
    brand: 0.4,
    health: 0.2,
    convenience: 0.3,
    availability: 0.4,
    promotions: 0.2,
    family: 0.3,
    size: 0.2,
    cold: 0.3,
  };

  // Price sensitivity increases price and promotions importance
  if (priceSensitivity > 0.6) {
    factors.price += 0.4;
    factors.promotions += 0.3;
  } else if (priceSensitivity < 0.4) {
    factors.price -= 0.1;
    factors.brand += 0.2;
  }

  // Brand loyalty increases brand and taste importance
  if (brandLoyalty > 0.6) {
    factors.brand += 0.3;
    factors.taste += 0.2;
  }

  // Lifestyle segment impacts
  if (selections.lifestyleSegment === 'health_conscious') {
    factors.health += 0.5;
    factors.taste -= 0.1;
  } else if (selections.lifestyleSegment === 'price_focused') {
    factors.price += 0.3;
    factors.promotions += 0.2;
  } else if (selections.lifestyleSegment === 'premium') {
    factors.brand += 0.3;
    factors.taste += 0.2;
    factors.price -= 0.2;
  } else if (selections.lifestyleSegment === 'traditional') {
    factors.family += 0.3;
    factors.brand += 0.2;
  }

  // Age impacts
  const hasYoung = selections.ageRanges.includes('18-28');
  const hasSenior = selections.ageRanges.includes('55+');
  if (hasYoung) {
    factors.taste += 0.2;
    factors.convenience += 0.2;
  }
  if (hasSenior) {
    factors.health += 0.2;
    factors.family += 0.2;
  }

  // Children impacts
  if (selections.hasChildren === 'yes') {
    factors.family += 0.3;
    factors.size += 0.2;
    factors.price += 0.1;
  }

  // Channel impacts
  if (selections.channelPreference === 'convenience') {
    factors.convenience += 0.3;
    factors.cold += 0.3;
  } else if (selections.channelPreference === 'traditional') {
    factors.availability += 0.2;
    factors.cold += 0.2;
  }

  // Consumption frequency impacts
  if (selections.consumptionFrequency === 'daily') {
    factors.availability += 0.2;
    factors.price += 0.1;
  }

  // Convert to array and sort by importance
  const factorLabels = language === 'es' ?
    Object.fromEntries(Object.entries(DECISION_FACTORS).map(([k, v]) => [k, v.es])) :
    Object.fromEntries(Object.entries(DECISION_FACTORS).map(([k, v]) => [k, v.en]));

  return Object.entries(factors)
    .map(([key, importance]) => ({
      factor: factorLabels[key] || key,
      importance: Math.min(1, Math.max(0, importance)),
    }))
    .sort((a, b) => b.importance - a.importance)
    .slice(0, 5); // Top 5 factors
}

/**
 * Calculate media channel affinities based on demographics
 * Returns sorted array of channels with affinity scores
 */
function calculateMediaChannels(
  selections: ProfileSelections,
  language: string
): Array<{ channel: string; affinity: number }> {
  const channels: Record<string, number> = {
    tv: 0.5,
    social_media: 0.4,
    radio: 0.3,
    ooh: 0.4,
    digital: 0.3,
    pos: 0.5,
    influencers: 0.2,
    word_of_mouth: 0.4,
    streaming: 0.3,
    print: 0.2,
  };

  // Age impacts media preferences
  const hasYoung = selections.ageRanges.includes('18-28') || selections.ageRanges.includes('25-35');
  const hasMidAge = selections.ageRanges.includes('35-45') || selections.ageRanges.includes('45-55');
  const hasSenior = selections.ageRanges.includes('55+');

  if (hasYoung) {
    channels.social_media += 0.4;
    channels.influencers += 0.4;
    channels.streaming += 0.3;
    channels.digital += 0.3;
    channels.tv -= 0.1;
    channels.print -= 0.1;
  }
  if (hasMidAge) {
    channels.tv += 0.2;
    channels.digital += 0.2;
    channels.radio += 0.1;
  }
  if (hasSenior) {
    channels.tv += 0.3;
    channels.radio += 0.3;
    channels.print += 0.2;
    channels.social_media -= 0.2;
    channels.streaming -= 0.2;
  }

  // Location impacts
  if (selections.locationTypes.includes('rural')) {
    channels.radio += 0.3;
    channels.tv += 0.2;
    channels.ooh -= 0.2;
    channels.digital -= 0.2;
  }
  if (selections.locationTypes.includes('urban') && selections.locationTypes.length <= 2) {
    channels.ooh += 0.2;
    channels.digital += 0.2;
    channels.streaming += 0.2;
  }

  // Education impacts
  if (selections.educationLevel === 'university' || selections.educationLevel === 'postgraduate') {
    channels.digital += 0.2;
    channels.streaming += 0.2;
  }

  // Occupation impacts
  if (selections.occupation === 'student') {
    channels.social_media += 0.3;
    channels.streaming += 0.3;
  } else if (selections.occupation === 'homemaker') {
    channels.tv += 0.3;
    channels.radio += 0.2;
  }

  // Lifestyle impacts
  if (selections.lifestyleSegment === 'modern') {
    channels.digital += 0.3;
    channels.social_media += 0.2;
  } else if (selections.lifestyleSegment === 'traditional') {
    channels.tv += 0.2;
    channels.radio += 0.2;
    channels.word_of_mouth += 0.2;
  }

  // Channel preference impacts
  if (selections.channelPreference === 'ecommerce') {
    channels.digital += 0.3;
    channels.social_media += 0.2;
  } else if (selections.channelPreference === 'traditional') {
    channels.pos += 0.3;
    channels.word_of_mouth += 0.2;
  } else if (selections.channelPreference === 'convenience') {
    channels.pos += 0.2;
    channels.ooh += 0.2;
  }

  // Convert to array and sort
  const channelLabels = language === 'es' ?
    Object.fromEntries(Object.entries(MEDIA_CHANNELS).map(([k, v]) => [k, v.es])) :
    Object.fromEntries(Object.entries(MEDIA_CHANNELS).map(([k, v]) => [k, v.en]));

  return Object.entries(channels)
    .map(([key, affinity]) => ({
      channel: channelLabels[key] || key,
      affinity: Math.min(1, Math.max(0, affinity)),
    }))
    .sort((a, b) => b.affinity - a.affinity)
    .slice(0, 5); // Top 5 channels
}

/**
 * Generate best time to reach based on demographics
 */
function calculateBestTimeToReach(selections: ProfileSelections, language: string): string {
  const hasYoung = selections.ageRanges.includes('18-28');
  const hasSenior = selections.ageRanges.includes('55+');
  const t = language === 'es';

  if (selections.occupation === 'student') {
    return t ? 'Tardes y noches (después de clases)' : 'Afternoons & evenings (after classes)';
  }
  if (selections.occupation === 'homemaker') {
    return t ? 'Mañanas y mediodía' : 'Mornings & midday';
  }
  if (selections.occupation === 'employee') {
    return t ? 'Hora de comida y noches' : 'Lunch hour & evenings';
  }
  if (hasYoung) {
    return t ? 'Noches y fines de semana' : 'Evenings & weekends';
  }
  if (hasSenior) {
    return t ? 'Mañanas y mediodía' : 'Mornings & midday';
  }
  return t ? 'Hora de comida (12-3pm)' : 'Meal times (12-3pm)';
}

/**
 * Generate messaging tips based on demographics
 */
function generateMessagingTips(
  selections: ProfileSelections,
  priceSensitivity: number,
  brandLoyalty: number,
  language: string
): string[] {
  const tips: string[] = [];
  const t = language === 'es';

  // Price sensitivity tips
  if (priceSensitivity > 0.6) {
    tips.push(t ? 'Destacar valor y promociones' : 'Emphasize value and promotions');
    tips.push(t ? 'Mostrar comparaciones de precio' : 'Show price comparisons');
  }

  // Brand loyalty tips
  if (brandLoyalty > 0.6) {
    tips.push(t ? 'Reforzar herencia y tradición de marca' : 'Reinforce brand heritage and tradition');
    tips.push(t ? 'Usar testimonios de familias' : 'Use family testimonials');
  } else if (brandLoyalty < 0.4) {
    tips.push(t ? 'Ofrecer prueba de producto' : 'Offer product trial');
    tips.push(t ? 'Comparar atributos vs competencia' : 'Compare attributes vs competition');
  }

  // Lifestyle tips
  if (selections.lifestyleSegment === 'health_conscious') {
    tips.push(t ? 'Destacar opciones sin azúcar' : 'Highlight zero-sugar options');
    tips.push(t ? 'Comunicar información nutricional' : 'Communicate nutritional info');
  } else if (selections.lifestyleSegment === 'traditional') {
    tips.push(t ? 'Conectar con momentos familiares' : 'Connect with family moments');
    tips.push(t ? 'Usar imágenes de celebraciones' : 'Use celebration imagery');
  } else if (selections.lifestyleSegment === 'modern') {
    tips.push(t ? 'Usar contenido dinámico y visual' : 'Use dynamic, visual content');
    tips.push(t ? 'Destacar innovación y modernidad' : 'Highlight innovation and modernity');
  }

  // Age tips
  const hasYoung = selections.ageRanges.includes('18-28');
  if (hasYoung) {
    tips.push(t ? 'Usar influencers y UGC' : 'Use influencers and UGC');
    tips.push(t ? 'Formato corto y entretenido' : 'Short, entertaining format');
  }

  // Children tips
  if (selections.hasChildren === 'yes') {
    tips.push(t ? 'Mostrar momentos familiares' : 'Show family moments');
    tips.push(t ? 'Destacar tamaños familiares' : 'Highlight family sizes');
  }

  return tips.slice(0, 4); // Top 4 tips
}

// ============================================================================
// AVATAR MAPPING
// ============================================================================

function getAvatar(ageRanges: string[], gender: string, locationTypes: string[]): string {
  const isYoung = ageRanges.includes('18-28') || ageRanges.includes('25-35');
  const isUrban = locationTypes.includes('urban');

  if (gender === 'female') {
    if (isYoung) return isUrban ? '👩‍💼' : '👩';
    return '👩‍👧';
  }
  if (gender === 'male') {
    if (isYoung) return isUrban ? '👨‍💻' : '👨‍🎓';
    return '👨‍👧';
  }
  // 'all' or other
  if (isYoung) return isUrban ? '👩‍💼' : '👨‍🎓';
  return '👴';
}

// ============================================================================
// MATCHING LOGIC
// ============================================================================

/**
 * Find the segment that best matches the user's selections
 * Now uses calculated price sensitivity and brand loyalty
 */
function findBestSegment(
  selections: ProfileSelections,
  segments: Segment[],
  calculatedPriceSens: number,
  calculatedBrandLoyalty: number
): { segment: Segment; score: number } {
  if (segments.length === 0) {
    throw new Error('No segments available');
  }

  let bestMatch = { segment: segments[0], score: 0 };

  for (const segment of segments) {
    // Match based on calculated outputs
    const priceSensScore = 1 - Math.abs(calculatedPriceSens - segment.price_sensitivity);
    const loyaltyScore = 1 - Math.abs(calculatedBrandLoyalty - segment.brand_loyalty);

    // Income matching
    const incomeOrder = ['D+', 'D', 'C-', 'C', 'C+', 'B', 'A/B'];
    const selIdx = selections.incomeLevel === 'all' ? 3 : incomeOrder.indexOf(selections.incomeLevel); // 'all' uses middle (C)
    const segmentIncome = segment.demographics?.income || 'C';
    const segIdx = incomeOrder.indexOf(segmentIncome);
    const incomeScore = selections.incomeLevel === 'all'
      ? 0.7 // Neutral match for 'all'
      : (segIdx !== -1 ? 1 - Math.abs(selIdx - segIdx) / incomeOrder.length : 0.5);

    // Weighted average
    const score =
      priceSensScore * 0.35 +
      loyaltyScore * 0.35 +
      incomeScore * 0.3;

    if (score > bestMatch.score) {
      bestMatch = { segment, score };
    }
  }

  return bestMatch;
}

/**
 * Generate a quote based on segment and brand
 */
function generateQuote(
  segmentId: string,
  brand: string,
  language: string
): string {
  const templates = QUOTE_TEMPLATES[segmentId] || QUOTE_TEMPLATES.default;
  const langTemplates = language === 'es' ? templates.es : templates.en;
  const template = langTemplates[Math.floor(Math.random() * langTemplates.length)];
  return template.replace('{brand}', brand || 'this brand');
}

/**
 * Generate a name for the derived persona
 */
function generateName(
  matchedSegment: string,
  ageRanges: string[],
  language: string
): string {
  // Use midpoint of first selected age range
  const primaryAge = ageRanges[0] || '25-35';
  const midAge = primaryAge.includes('+')
    ? parseInt(primaryAge) + 5
    : Math.round((parseInt(primaryAge.split('-')[0]) + parseInt(primaryAge.split('-')[1])) / 2);

  if (language === 'es') {
    return `${matchedSegment}, ${midAge} años`;
  }
  return `${matchedSegment}, ${midAge}`;
}

/**
 * Get location display name (handles multiple selections)
 */
function getLocationDisplay(locationTypes: string[], language: string): string {
  const locations: Record<string, { en: string; es: string }> = {
    urban: { en: 'Urban', es: 'Urbano' },
    suburban: { en: 'Suburban', es: 'Suburbano' },
    rural: { en: 'Rural', es: 'Rural' },
    urban_periphery: { en: 'Urban Periphery', es: 'Periferia Urbana' },
  };

  if (locationTypes.length === 4) {
    return language === 'es' ? 'Todos' : 'All';
  }

  const names = locationTypes.map(
    loc => locations[loc]?.[language === 'es' ? 'es' : 'en'] || loc
  );
  return names.join(', ');
}

/**
 * Get age range display (handles multiple selections)
 */
function getAgeRangeDisplay(ageRanges: string[], language: string): string {
  if (ageRanges.length === 5) {
    return language === 'es' ? 'Todos' : 'All';
  }
  return ageRanges.join(', ');
}

// ============================================================================
// MAIN HOOK
// ============================================================================

export function useProfileGeneration(segments: Segment[], language: string = 'en') {
  /**
   * Generate a derived persona from user selections
   * Price sensitivity and brand loyalty are CALCULATED, not user inputs
   */
  const generateProfile = useCallback(
    (selections: ProfileSelections): DerivedPersona => {
      // Calculate price sensitivity and brand loyalty as OUTPUTS
      const calculatedPriceSens = calculatePriceSensitivity(selections);
      const calculatedBrandLoyalty = calculateBrandLoyalty(selections);

      const { segment, score } = findBestSegment(
        selections,
        segments,
        calculatedPriceSens,
        calculatedBrandLoyalty
      );

      const primaryAge = selections.ageRanges[0] || '25-35';
      const midAge = primaryAge.includes('+')
        ? parseInt(primaryAge) + 5
        : Math.round(
            (parseInt(primaryAge.split('-')[0]) +
              parseInt(primaryAge.split('-')[1])) /
              2
          );

      // Determine purchase frequency based on consumption frequency input
      let purchaseFrequency = language === 'es' ? 'Semanal' : 'Weekly';
      if (selections.consumptionFrequency === 'daily') {
        purchaseFrequency = language === 'es' ? 'Diario' : 'Daily';
      } else if (selections.consumptionFrequency === 'several_weekly') {
        purchaseFrequency = language === 'es' ? '2-3 veces/semana' : '2-3 times/week';
      } else if (selections.consumptionFrequency === 'occasional' || selections.consumptionFrequency === 'rarely') {
        purchaseFrequency = language === 'es' ? 'Quincenal' : 'Bi-weekly';
      }

      // Determine basket size based on income and household
      const incomeOrder = ['D+', 'D', 'C-', 'C', 'C+', 'B', 'A/B'];
      const incomeIdx = selections.incomeLevel === 'all' ? 3 : incomeOrder.indexOf(selections.incomeLevel);
      let avgBasketSize = language === 'es' ? 'Mediano' : 'Medium';
      if (selections.incomeLevel !== 'all' && incomeIdx <= 2) {
        avgBasketSize = language === 'es' ? 'Pequeño' : 'Small';
      } else if (selections.incomeLevel !== 'all' && incomeIdx >= 5) {
        avgBasketSize = language === 'es' ? 'Grande' : 'Large';
      }
      // Household size bonus
      if (selections.householdSize === '5+') {
        avgBasketSize = language === 'es' ? 'Grande' : 'Large';
      }

      // Channel from selection
      const channelLabels: Record<string, { en: string; es: string }> = {
        traditional: { en: 'Traditional store', es: 'Tienda tradicional' },
        modern: { en: 'Supermarket', es: 'Supermercado' },
        convenience: { en: 'Convenience (OXXO)', es: 'Conveniencia (OXXO)' },
        ontrade: { en: 'Restaurants', es: 'Restaurantes' },
        ecommerce: { en: 'E-commerce', es: 'E-commerce' },
        all: { en: 'Multiple', es: 'Múltiples' },
      };
      const channelPreference = channelLabels[selections.channelPreference]?.[language === 'es' ? 'es' : 'en'] || 'Multiple';

      return {
        id: `derived-${Date.now()}`,
        name: generateName(segment.name, selections.ageRanges, language),
        age: midAge,
        location: getLocationDisplay(selections.locationTypes, language),
        segment: segment.name,
        segmentColor: undefined,
        quote: generateQuote(segment.id, selections.brandPreference, language),
        avatar: getAvatar(selections.ageRanges, selections.gender, selections.locationTypes),
        metrics: {
          population: Math.round(segment.share * 100),
          loyalty: Math.round(calculatedBrandLoyalty * 100), // Now an OUTPUT
          frequency: selections.consumptionFrequency === 'daily' ? 5 : 2,
        },
        topics: segment.characteristics?.slice(0, 3) || [],
        channels: [channelPreference],
        derived: true,
        matchedSegment: segment.name,
        matchedSegmentId: segment.id,
        matchScore: score,
        selections,
        derivedMetrics: {
          estimatedShare: segment.share,
          purchaseFrequency,
          avgBasketSize,
          channelPreference,
        },
        // NEW: Calculated outputs
        calculatedPriceSensitivity: calculatedPriceSens,
        calculatedBrandLoyalty: calculatedBrandLoyalty,
        // NEW: Targeting information
        targetingInfo: {
          keyFactors: calculateKeyFactors(selections, calculatedPriceSens, calculatedBrandLoyalty, language),
          mediaChannels: calculateMediaChannels(selections, language),
          bestTimeToReach: calculateBestTimeToReach(selections, language),
          messagingTips: generateMessagingTips(selections, calculatedPriceSens, calculatedBrandLoyalty, language),
        },
      };
    },
    [segments, language]
  );

  /**
   * Compare two derived personas
   */
  const compareProfiles = useCallback(
    (profileA: DerivedPersona, profileB: DerivedPersona): ComparisonResult => {
      // Use calculated values for comparison
      const priceSensitivityDiff =
        (profileA.calculatedPriceSensitivity || 0.5) - (profileB.calculatedPriceSensitivity || 0.5);
      const brandLoyaltyDiff =
        (profileA.calculatedBrandLoyalty || 0.5) - (profileB.calculatedBrandLoyalty || 0.5);
      const sameSegment = profileA.matchedSegmentId === profileB.matchedSegmentId;

      const keyDifferences: string[] = [];
      const t = language === 'es';

      if (Math.abs(priceSensitivityDiff) > 0.1) {
        const higher = priceSensitivityDiff > 0 ? 'A' : 'B';
        const pct = Math.abs(Math.round(priceSensitivityDiff * 100));
        keyDifferences.push(
          t
            ? `Perfil ${higher} es ${pct}% más sensible al precio`
            : `Profile ${higher} is ${pct}% more price sensitive`
        );
      }

      if (Math.abs(brandLoyaltyDiff) > 0.1) {
        const higher = brandLoyaltyDiff > 0 ? 'A' : 'B';
        const pct = Math.abs(Math.round(brandLoyaltyDiff * 100));
        keyDifferences.push(
          t
            ? `Perfil ${higher} tiene ${pct}% más lealtad de marca`
            : `Profile ${higher} has ${pct}% higher brand loyalty`
        );
      }

      if (sameSegment) {
        keyDifferences.push(
          t
            ? `Ambos perfiles coinciden con "${profileA.matchedSegment}"`
            : `Both profiles match "${profileA.matchedSegment}" segment`
        );
      } else {
        keyDifferences.push(
          t
            ? `Perfil A: ${profileA.matchedSegment} vs Perfil B: ${profileB.matchedSegment}`
            : `Profile A: ${profileA.matchedSegment} vs Profile B: ${profileB.matchedSegment}`
        );
      }

      return {
        priceSensitivityDiff,
        brandLoyaltyDiff,
        sameSegment,
        keyDifferences,
      };
    },
    [language]
  );

  return {
    generateProfile,
    compareProfiles,
  };
}
